// @ts-nocheck
'use client'

import { useState, useEffect } from 'react'
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import GroupModal from 'components/modals/GroupModal'
import ActionCardComponent from 'components/common/ActionCardComponent'
import TooltipComponent from 'components/common/TooltipComponent'
import PageLoadingState from 'components/common/PageLoadingState'

import { useAuth } from 'auth/AuthContext'

const GroupsManagement = ({ onBlockGroupsUpdate }) => {
  const { user } = useAuth()
  const [state, setState] = useState({
    blockGroups: [],
    error: null,
    isOpenGroupModal: false,
    selectedGroup: null,
    groupsSearchTerm: '',
    loading: true
  })

  const getBlockGroups = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/farming-blocks/block-groups/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })

      const groups = Array.isArray(response.data) ? response.data : []
      setState(s => ({
        ...s,
        blockGroups: groups,
        error: null,
        loading: false
      }))
      // Notify parent component of block groups update
      if (onBlockGroupsUpdate) {
        onBlockGroupsUpdate(groups)
      }
    } catch (err) {
      showAlert({ message: 'Failed to load farming block groups', icon: 'error' })
      setState(s => ({
        ...s,
        error: 'Failed to load farming block groups',
        blockGroups: [],
        loading: false
      }))
    }
  }

  const handleSaveGroup = async (groupData) => {
    try {
      const isEditing = !!state?.selectedGroup?.blockGroupId

      const method = isEditing ? 'PUT' : 'POST'
      const url = isEditing
        ? `/management/farming-blocks/block-groups/${state.selectedGroup.blockGroupId}`
        : `/management/farming-blocks/block-groups/${user.farmId}`

      const response = await makeRequest({
        method,
        url,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        data: groupData,
        alertResult: true
      })

      if (response?.status === 200 || response?.status === 201) {
        await getBlockGroups()
        setState(s => ({
          ...s,
          isOpenGroupModal: false,
          selectedGroup: null
        }))
      }
    } catch (err) {
      showAlert({ message: 'Failed to save group', icon: 'error' })
      setState(s => ({
        ...s,
        error: 'Failed to save group'
      }))
    }
  }

  const handleDelete = async (group) => {
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/farming-blocks/block-groups/${group.blockGroupId || group.groupId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      })
      if (response?.status === 200) {
        getBlockGroups()
      }
    } catch (error) {
      showAlert({ text: 'Failed to delete group', icon: 'error' })
    }
  }

  const onToogleGroupModal = (group = null) => {
    setState(s => ({
      ...s,
      isOpenGroupModal: !s.isOpenGroupModal,
      selectedGroup: group
    }))
  }

  const filteredGroups = state.blockGroups.filter(group =>
    (group.groupName || '').toLowerCase().includes(state.groupsSearchTerm)
  )

  const groupsTableData = {
    columns: [
      { key: 'blockGroupId', label: '#' },
      { key: 'groupName', label: 'Group Name' },
      {
        key: 'actions',
        label: 'Manage',
        cell: (value, group) => (
          <div className='flex items-center gap-2'>
            <button onClick={() => onToogleGroupModal(group)} title='Edit' className='p-1 rounded transition'>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDelete(group)} title='Delete' className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: filteredGroups
  }

  useEffect(() => {
    if (!user?.farmId) return
    getBlockGroups()
  }, [user?.farmId])

  if (state.loading) {
    return <PageLoadingState message='Loading farming block groups...' rows={4} />
  }

  return (
    <>
      <ActionCardComponent>
        <input
          type='text'
          value={state.groupsSearchTerm}
          placeholder='Filter Group(s)'
          onChange={(e) => setState(s => ({ ...s, groupsSearchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />

        {!!user.farmId && (
          <TooltipComponent content='Introduce a new grouping option for assigning or filtering farming blocks.'>
            <ButtonComponent onClick={() => onToogleGroupModal()}>
              <PlusIcon className='w-5 h-5 mr-1' />
              New Group
            </ButtonComponent>
          </TooltipComponent>
        )}
      </ActionCardComponent>

      <TableComponent tableData={groupsTableData} />

      {/* group modal */}
      {state.isOpenGroupModal && (
        <GroupModal
          isOpen
          handleClose={onToogleGroupModal}
          handleSave={handleSaveGroup}
          selectedGroup={state.selectedGroup}
        />
      )}
    </>
  )
}

export default GroupsManagement
